'use client';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import { useState, useEffect, useRef } from 'react';

// Animated counter hook
function useCountUp(target: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

// Intersection observer hook
function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function AnimatedStat({ value, suffix, label }: {value: number;suffix: string;label: string;}) {
  const { ref, inView } = useInView(0.3);
  const count = useCountUp(value, 2000, inView);
  return (
    <div ref={ref} className="text-center">
      <div className="text-6xl md:text-7xl font-bold text-white mb-3 tabular-nums">
        {count.toLocaleString()}{suffix}
      </div>
      <p className="text-white/70 font-light text-sm leading-relaxed max-w-[200px] mx-auto">{label}</p>
    </div>);

}

const heartPillars = [
{
  letter: 'H',
  title: 'Health',
  desc: 'Teaching nutrition, preventive healthcare, wellness, and healthy lifestyle habits.',
  color: 'from-rose-500 to-rose-700',
  bg: 'bg-rose-50',
  border: 'border-rose-200',
  icon: '🫀'
},
{
  letter: 'E',
  title: 'Education',
  desc: 'Providing hands-on STEM learning through coding, engineering, plant science, and health literacy.',
  color: 'from-blue-500 to-blue-700',
  bg: 'bg-blue-50',
  border: 'border-blue-200',
  icon: '📚'
},
{
  letter: 'A',
  title: 'Access',
  desc: 'Expanding access to fresh produce, educational opportunities, and wellness resources.',
  color: 'from-emerald-500 to-emerald-700',
  bg: 'bg-emerald-50',
  border: 'border-emerald-200',
  icon: '🌱'
},
{
  letter: 'R',
  title: 'Research',
  desc: 'Encouraging innovation, problem-solving, data collection, and evidence-based thinking.',
  color: 'from-violet-500 to-violet-700',
  bg: 'bg-violet-50',
  border: 'border-violet-200',
  icon: '🔬'
},
{
  letter: 'T',
  title: 'Togetherness',
  desc: 'Bringing together schools, families, healthcare organizations, community partners, and local leaders.',
  color: 'from-amber-500 to-amber-700',
  bg: 'bg-amber-50',
  border: 'border-amber-200',
  icon: '🤝'
}];


const whyItMattersCards = [
{
  icon: '⚖️',
  title: 'Health Equity',
  desc: 'Expanding access to nutritious food and wellness education.',
  accent: '#C9A96E'
},
{
  icon: '💻',
  title: 'STEM Education',
  desc: 'Teaching coding, engineering, automation, data literacy, and problem-solving.',
  accent: '#3B82F6'
},
{
  icon: '🥬',
  title: 'Food Access',
  desc: 'Growing and distributing fresh produce to families and communities.',
  accent: '#22C55E'
},
{
  icon: '🚀',
  title: 'Workforce Development',
  desc: 'Introducing students to careers in healthcare, biotechnology, engineering, and entrepreneurship.',
  accent: '#8B5CF6'
},
{
  icon: '🏘️',
  title: 'Community Well-Being',
  desc: 'Strengthening schools as trusted centers for health, education, and opportunity.',
  accent: '#F97316'
}];


const programHighlights = [
{
  icon: '🥗',
  title: 'Health & Nutrition Education',
  desc: 'Students learn how nutrition impacts physical health, academic performance, chronic disease prevention, and overall wellness.',
  img: '/assets/images/Screenshot_2026-06-07_at_3.15.28_PM-1780859736356.png',
  alt: 'Heart-shaped arrangement of colorful fresh fruits and vegetables representing health and nutrition'
},
{
  icon: '⚙️',
  title: 'STEM & Technology Training',
  desc: 'Students build foundational skills in coding, engineering, automation, environmental monitoring, data literacy, and problem-solving.',
  img: '/assets/images/Screenshot_2026-06-07_at_2.46.46_PM-1780858013898.png',
  alt: 'Lab No. 4 STEM & Technology Training worksheet for students'
},
{
  icon: '🌾',
  title: 'Food Access & Community Impact',
  desc: 'Fresh produce grown through the program is distributed to families and community members, increasing access to nutritious food.',
  img: '/assets/images/Screenshot_2026-03-25_at_10.02.03_PM-1774490528099.png',
  alt: 'Community food distribution event with fresh produce'
},
{
  icon: '🎯',
  title: 'Future Workforce Development',
  desc: 'Students gain exposure to careers in healthcare, biotechnology, engineering, agriculture technology, sustainability, and entrepreneurship.',
  img: '/assets/images/Screenshot_2026-06-07_at_3.46.56_PM-1780861665253.png',
  alt: 'Mary and students assembling a hydroponic tower'
}];


const timelinePhases = [
{
  weeks: 'Weeks 1–4',
  title: 'Food Systems, Health & Equity',
  desc: 'Students explore food insecurity, nutrition, community health, food deserts, plant biology, and the factors that influence health outcomes.',
  topics: ['Food systems', 'Food deserts', 'Community health', 'Plant biology', 'Nutrition science', 'Systems thinking'],
  color: 'bg-emerald-500',
  lightColor: 'bg-emerald-50',
  borderColor: 'border-emerald-200',
  textColor: 'text-emerald-700',
  img: '/assets/images/Screenshot_2026-06-07_at_3.52.44_PM-1780861983124.png',
  imgAlt: 'Mary teaching students in a classroom during the Food Systems, Health & Equity phase'
},
{
  weeks: 'Weeks 5–10',
  title: 'Computational Thinking & Coding',
  desc: 'Students learn programming concepts and develop technical problem-solving skills.',
  topics: ['Logic', 'Data', 'Algorithms', 'Coding foundations', 'Computer systems'],
  color: 'bg-blue-500',
  lightColor: 'bg-blue-50',
  borderColor: 'border-blue-200',
  textColor: 'text-blue-700',
  img: '/assets/images/Food_For_Thought_Foundation_teaching_Kids_STEM_and_Building_Hydroponics-1774490919106.png',
  imgAlt: 'Students coding and learning computational thinking on laptops'
},
{
  weeks: 'Weeks 11–21',
  title: 'Engineering Smart Systems',
  desc: 'Students connect software and hardware through sensors, automation, embedded systems, and environmental monitoring technologies.',
  topics: ['Sensors', 'Electronics', 'Automation', 'Embedded systems', 'Hardware integration'],
  color: 'bg-violet-500',
  lightColor: 'bg-violet-50',
  borderColor: 'border-violet-200',
  textColor: 'text-violet-700',
  img: '/assets/images/Screenshot_2026-03-25_at_10.02.41_PM-1774490572490.png',
  imgAlt: 'Students engineering smart hydroponic systems with sensors and automation'
},
{
  weeks: 'Weeks 22–28',
  title: 'Innovation Capstone',
  desc: 'Students design and present solutions that address real-world challenges related to food systems, health, and sustainability.',
  topics: ['Innovation', 'Teamwork', 'Design thinking', 'Public speaking', 'Community impact'],
  color: 'bg-amber-500',
  lightColor: 'bg-amber-50',
  borderColor: 'border-amber-200',
  textColor: 'text-amber-700',
  img: '/assets/images/Screenshot_2026-06-07_at_3.49.55_PM-1780861814802.png',
  imgAlt: 'Students posing with a hydroponic tower in a classroom during the Innovation Capstone phase'
}];


const produce = [
  { emoji: '🥬', name: 'Lettuce' },
  { emoji: '🌱', name: 'Mustard Greens' },
  { emoji: '🥬', name: 'Cabbage' },
  { emoji: '🍅', name: 'Tomatoes' },
  { emoji: '🫑', name: 'Peppers' },
  { emoji: '🌿', name: 'Herbs' },
  { emoji: '🫛', name: 'Green Beans' },
  { emoji: '🍠', name: 'Sweet Potatoes' },
  { emoji: '🌿', name: 'Okra' },
  { emoji: '🥬', name: 'Chard' },
  { emoji: '🫘', name: 'Legumes' },
];


const fiveYearStats = [
{ value: 62, suffix: '', label: 'Counties Reached Across New York State' },
{ value: 62, suffix: '', label: 'School Health Hubs Established' },
{ value: 148800, suffix: '+', label: 'Pounds of Fresh Produce Grown Every Year' },
{ value: 48360, suffix: '+', label: 'Family Food Distributions Every Year' }];


const photoGalleries = [
{
  title: 'Partners in Impact',
  subtitle: 'Gallery 4',
  desc: 'Schools, educators, sponsors, university partners, and community organizations.',
  photos: [
  { src: '/assets/images/Screenshot_2026-03-24_at_9.54.47_PM-1774403695605.png', alt: 'Mary Bellakbira with USC Viterbi engineering partners at collaboration meeting' },
  { src: '/assets/images/Screenshot_2026-03-24_at_9.58.08_PM-1774403894596.png', alt: 'Educators and community partners at Food for Thought Foundation event' },
  { src: '/assets/images/Screenshot_2026-03-24_at_10.39.22_PM-1774406395466.png', alt: 'School administrators and program partners reviewing hydroponic curriculum' },
  { src: '/assets/images/openart-image_1774025863080_f71b84e2_1774025863380_aec756e2-1774400011320.png', alt: 'Community stakeholders and partner organizations at program launch event' }]

}];


export default function FoodForThoughtPage() {
  const [activeGallery, setActiveGallery] = useState(0);

  return (
    <>
      <Header />
      <main className="bg-white overflow-x-hidden">

        {/* ── HERO SECTION ── */}
        <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
          {/* Full-bleed hero image */}
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/images/Food_For_Thought_Foundation_teaching_Kids_STEM_and_Building_Hydroponics-1774490919106.png"
              alt="Students working with hydroponic towers in a school innovation lab"
              className="w-full h-full object-cover" />

            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />
          </div>

          {/* Back nav */}
          <div className="absolute top-28 left-6 z-20">
            <Link href="/bold-through-heart" className="inline-flex items-center gap-2 text-white/60 text-micro tracking-widest uppercase hover:text-white transition-colors duration-200">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Lead with H.E.A.R.T.
            </Link>
          </div>

          {/* Hero content */}
          <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20 pt-40 w-full">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-white/80 text-micro tracking-widest uppercase">School Health Hubs · Lead with H.E.A.R.T. Initiative</span>
              </div>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-[0.95] tracking-tight">
                Where Health,<br />
                <span className="italic font-light text-[#C9A96E]">Innovation,</span><br />
                and Opportunity Grow
              </h1>
              <p className="text-lg md:text-xl text-white/75 font-light leading-relaxed max-w-2xl mb-4">
                A statewide Lead with H.E.A.R.T. initiative by <strong className="text-white font-semibold">Mary Bellakbira</strong>, Miss Heart of New York 2026.
              </p>
              <p className="text-base text-white/60 font-light leading-relaxed max-w-2xl mb-10">Launching School Health Hubs across New York's 10 regions & 62 Counties transforming schools into centers of health, STEM innovation, food access, and community impact.

              </p>
              <div className="flex flex-wrap gap-4">
                <Link href="/contact" className="px-8 py-4 bg-[#C9A96E] text-white rounded-full font-semibold text-sm tracking-wide hover:bg-[#b8935a] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5">
                  Bring a Hub to Your School
                </Link>
                <Link href="/partnerships-impact" className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white rounded-full font-semibold text-sm tracking-wide hover:bg-white/20 transition-all duration-300">
                  Partner With Us
                </Link>
                <Link href="/contact" className="px-8 py-4 border border-white/30 text-white rounded-full font-semibold text-sm tracking-wide hover:border-white/60 hover:bg-white/5 transition-all duration-300">
                  Support the Mission
                </Link>
              </div>
            </div>
          </div>

          {/* Supporting photo strip */}
          <div className="relative z-10 grid grid-cols-3 gap-0 h-40 md:h-56">
            {[
            { src: '/assets/images/Screenshot_2026-03-25_at_10.02.03_PM-1774490528099.png', alt: 'Students distributing fresh produce to community families' },
            { src: '/assets/images/kiddos-1774394758172.png', alt: 'Students coding and programming in STEM classroom' },
            { src: '/assets/images/Cornell_Grad_Shoot_1_-1774491105610.jpeg', alt: 'Students engaged in hands-on classroom learning and innovation' }].
            map((photo, i) =>
            <div key={i} className="relative overflow-hidden">
                <img src={photo.src} alt={photo.alt} className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-500 scale-105 hover:scale-100" />
                <div className="absolute inset-0 bg-black/30" />
              </div>
            )}
          </div>
        </section>

        {/* ── WHY IT MATTERS ── */}
        <section className="py-28 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-micro text-[#C9A96E] tracking-widest uppercase mb-4">Why It Matters</p>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                Healthcare Starts Before<br />
                <span className="italic font-light">the Doctor's Office</span>
              </h2>
              <p className="text-lg text-foreground/65 font-light leading-relaxed max-w-3xl mx-auto">
                Access to nutritious food, health education, and community resources plays a major role in long-term health outcomes. Yet many children live in communities where healthy food options and preventive wellness resources are limited.
              </p>
            </div>

            {/* Food Desert Stats Banner */}
            <div className="mb-16 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-800 to-emerald-950 p-10 flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10">
                  <div className="text-6xl md:text-7xl font-black text-white mb-3 leading-none">23M</div>
                  <p className="text-emerald-200 text-lg font-light leading-relaxed">Americans live in a food desert <span className="text-white font-semibold">today</span>.</p>
                </div>
                <div className="relative z-10 mt-8 h-0.5 w-16 rounded-full bg-[#C9A96E]" />
              </div>
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1a1a2e] to-[#16213e] p-10 flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10">
                  <div className="text-6xl md:text-7xl font-black text-[#C9A96E] mb-3 leading-none">85%</div>
                  <p className="text-white/70 text-lg font-light leading-relaxed">of NY's food desert residents are <span className="text-white font-semibold">BIPOC and Low-Income</span>.</p>
                </div>
                <div className="relative z-10 mt-8 h-0.5 w-16 rounded-full bg-[#C9A96E]" />
              </div>
            </div>

            {/* Student Voice Quote Image */}
            <div className="mb-16 flex justify-center">
              <div className="relative max-w-2xl w-full rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/assets/images/Screenshot_2026-06-07_at_3.29.57_PM-1780860611135.png"
                  alt="Student quote from Kevonte at HD Woodson HS: All I've ever seen in my neighborhood are liquor and convenience stores."
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>

            {/* 5 icon cards — asymmetric bento */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {whyItMattersCards.slice(0, 3).map((card, i) =>
              <div
                key={i}
                className="p-8 bg-[#F5F0EB] rounded-3xl border border-[#E8E2DC] hover:shadow-xl transition-all duration-500 hover:-translate-y-1">

                  <div className="text-4xl mb-5">{card.icon}</div>
                  <h3 className="text-xl font-bold text-foreground mb-3">{card.title}</h3>
                  <p className="text-foreground/60 font-light leading-relaxed text-sm">{card.desc}</p>
                  <div className="mt-5 h-0.5 w-12 rounded-full" style={{ backgroundColor: card.accent }} />
                </div>
              )}
            </div>
            <div className="flex flex-col md:flex-row justify-center gap-5 mt-5">
              {whyItMattersCards.slice(3).map((card, i) =>
              <div
                key={i}
                className="p-8 bg-[#F5F0EB] rounded-3xl border border-[#E8E2DC] hover:shadow-xl transition-all duration-500 hover:-translate-y-1 w-full md:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]">

                  <div className="text-4xl mb-5">{card.icon}</div>
                  <h3 className="text-xl font-bold text-foreground mb-3">{card.title}</h3>
                  <p className="text-foreground/60 font-light leading-relaxed text-sm">{card.desc}</p>
                  <div className="mt-5 h-0.5 w-12 rounded-full" style={{ backgroundColor: card.accent }} />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── MISSION STATEMENT ── */}
        <section className="py-28 px-6 bg-[#F5F0EB]">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-center">
              <div className="lg:col-span-3">
                <p className="text-micro text-[#C9A96E] tracking-widest uppercase mb-5">OUR ANSWER</p>
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-8 leading-tight">
                  School Health Hubs<br />
                  <span className="italic font-light">Through Hydroponics & The Food For Thought Foundation </span>
                </h2>
                <p className="text-xl text-foreground/75 font-light leading-relaxed mb-6">
                  School Health Hubs are more than school gardens. They are future-focused learning environments where students become innovators, problem-solvers, and community health leaders.
                </p>
                <p className="text-lg text-foreground/60 font-light leading-relaxed mb-8">
                  By combining food access, preventive health education, STEM learning, and workforce development, this initiative helps students understand that healthcare does not begin in a hospital. It begins in our homes, schools, and communities.
                </p>
                <div className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-[#E8E2DC]">
                  <div className="text-3xl">🌱</div>
                  <p className="text-foreground/70 font-light text-sm leading-relaxed italic">
                    "Healthcare does not begin in a hospital. It begins in our homes, schools, and communities."
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="/assets/images/Screenshot_2026-03-25_at_10.02.03_PM-1774490528099.png"
                    alt="Students harvesting fresh produce from hydroponic system in school"
                    className="w-full h-full object-cover" />

                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FULL WIDTH HARVEST IMAGE ── */}
        <div className="relative h-72 md:h-96 overflow-hidden">
          <img
            src="/assets/images/kiddos-1774394758172.png"
            alt="Students harvesting fresh produce from school hydroponic farm"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60" />
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-white text-2xl md:text-4xl font-bold tracking-wide text-center px-6">
              Growing Food. Building Futures. Strengthening Communities.
            </p>
          </div>
        </div>

        {/* ── PROGRAM HIGHLIGHTS ── */}
        <section className="py-28 px-6 bg-emerald-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-micro text-[#C9A96E] tracking-widest uppercase mb-4">Program Highlights</p>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                Four Pillars of<br />
                <span className="italic font-light">Student Transformation</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {programHighlights.map((item, i) =>
              <div key={i} className="group relative rounded-3xl overflow-hidden border border-[#E8E2DC] hover:shadow-2xl transition-all duration-500">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={item.img} alt={item.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="text-3xl mb-3">{item.icon}</div>
                    <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-white/70 font-light text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── ONE HYDROPONIC CURRICULUM ── */}
        <section className="py-28 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            {/* Section header */}
            <div className="mb-14">
              <p className="text-micro text-[#C9A96E] tracking-widest uppercase mb-4">School Health Hubs · Curriculum</p>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                One Hydroponic Curriculum.<br />
                <span className="italic font-light">A Lifetime of Impact.</span>
              </h2>
              <p className="text-lg text-foreground/65 font-light leading-relaxed mb-4">
                One School Health Hub is more than a garden. It is a hands-on classroom, a food access point, and a preventive health tool all in one.
              </p>
              <p className="text-lg text-foreground/65 font-light leading-relaxed">
                Through our hydroponic curriculum, students do not just learn about healthy food. They build the system that grows it.
              </p>
            </div>

            {/* What One Hub Can Do Every Month */}
            <div className="mb-14">
              <h3 className="text-2xl font-bold text-foreground mb-8">What One Hub Can Do Every Month</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex items-start gap-4 p-6 bg-emerald-50 border border-emerald-200 rounded-2xl">
                  <span className="text-3xl flex-shrink-0">🥬</span>
                  <div>
                    <p className="text-xl font-bold text-emerald-800">200 pounds</p>
                    <p className="text-foreground/65 font-light text-sm leading-relaxed">of fresh produce grown monthly</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-6 bg-emerald-50 border border-emerald-200 rounded-2xl">
                  <span className="text-3xl flex-shrink-0">👨‍👩‍👧‍👦</span>
                  <div>
                    <p className="text-xl font-bold text-emerald-800">65 families</p>
                    <p className="text-foreground/65 font-light text-sm leading-relaxed">supported every month</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-6 bg-emerald-50 border border-emerald-200 rounded-2xl">
                  <span className="text-3xl flex-shrink-0">🎓</span>
                  <div>
                    <p className="text-xl font-bold text-emerald-800">10 to 40 students</p>
                    <p className="text-foreground/65 font-light text-sm leading-relaxed">trained per curriculum cycle</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-6 bg-emerald-50 border border-emerald-200 rounded-2xl">
                  <span className="text-3xl flex-shrink-0">🏫</span>
                  <div>
                    <p className="text-xl font-bold text-emerald-800">Pre-K through 12th grade</p>
                    <p className="text-foreground/65 font-light text-sm leading-relaxed">programming, with opportunities for undergraduate students to mentor, research, and lead</p>
                  </div>
                </div>
              </div>
            </div>

            {/* How Students Learn */}
            <div className="mb-14">
              <h3 className="text-2xl font-bold text-foreground mb-4">How Students Learn</h3>
              <p className="text-foreground/65 font-light leading-relaxed mb-8">
                Students help build, plant, monitor, and maintain the hydroponic system while learning:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="p-6 bg-[#F5F0EB] border border-[#E8E2DC] rounded-2xl">
                  <p className="font-bold text-foreground mb-2">STEM</p>
                  <p className="text-foreground/65 font-light text-sm leading-relaxed">Plant science, engineering, water systems, coding, and data tracking</p>
                </div>
                <div className="p-6 bg-[#F5F0EB] border border-[#E8E2DC] rounded-2xl">
                  <p className="font-bold text-foreground mb-2">Health</p>
                  <p className="text-foreground/65 font-light text-sm leading-relaxed">Nutrition, wellness, food access, and preventive care</p>
                </div>
                <div className="p-6 bg-[#F5F0EB] border border-[#E8E2DC] rounded-2xl">
                  <p className="font-bold text-foreground mb-2">Leadership</p>
                  <p className="text-foreground/65 font-light text-sm leading-relaxed">Teamwork, responsibility, problem-solving, and community service</p>
                </div>
                <div className="p-6 bg-[#F5F0EB] border border-[#E8E2DC] rounded-2xl">
                  <p className="font-bold text-foreground mb-2">Innovation</p>
                  <p className="text-foreground/65 font-light text-sm leading-relaxed">How technology can solve real issues in hunger, health, and education</p>
                </div>
              </div>
            </div>

            {/* Why It Matters */}
            <div className="p-8 md:p-10 bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-3xl">
              <h3 className="text-2xl font-bold text-white mb-6">Why It Matters</h3>
              <p className="text-white/80 font-light leading-relaxed mb-5">
                When students build a hydroponic system, they are not just completing a lesson. They are creating a source of fresh food for their own school and community.
              </p>
              <p className="text-white/80 font-light leading-relaxed mb-5">
                Each curriculum cycle gives students the skills to understand where food comes from, how health is connected to access, and how innovation can serve people.
              </p>
              <p className="text-white font-semibold leading-relaxed">
                One hub can feed families today while teaching students skills they can carry for the rest of their lives.
              </p>
            </div>
          </div>
        </section>

        {/* ── YEAR ONE IMPACT ── */}
        <section className="py-28 px-6 bg-[#F5F0EB]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-micro text-[#C9A96E] tracking-widest uppercase mb-4">First 12 Weeks · Year One</p>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
                Year One Impact:<br />
                <span className="italic font-light">Where It All Begins</span>
              </h2>
              <p className="text-lg text-foreground/60 font-light leading-relaxed max-w-2xl mx-auto">
                In the first 12 weeks alone, the launch of 12 School Health Hubs will begin transforming communities across New York State.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Stat 1 */}
              <div className="p-8 bg-white rounded-3xl border border-[#E8E2DC] hover:shadow-xl transition-all duration-500 hover:-translate-y-1 text-center">
                <div className="text-5xl mb-4">🏫</div>
                <div className="text-5xl font-black text-emerald-700 mb-3">12</div>
                <p className="text-foreground/70 font-semibold text-sm leading-relaxed">School Health Hubs</p>
                <div className="mt-4 h-0.5 w-12 rounded-full bg-emerald-400 mx-auto" />
              </div>

              {/* Stat 2 */}
              <div className="p-8 bg-white rounded-3xl border border-[#E8E2DC] hover:shadow-xl transition-all duration-500 hover:-translate-y-1 text-center">
                <div className="text-5xl mb-4">🥬</div>
                <div className="text-5xl font-black text-emerald-700 mb-3">2,400<span className="text-3xl">+</span></div>
                <p className="text-foreground/70 font-semibold text-sm leading-relaxed">Pounds of Fresh Produce Grown Monthly</p>
                <div className="mt-4 h-0.5 w-12 rounded-full bg-emerald-400 mx-auto" />
              </div>

              {/* Stat 3 */}
              <div className="p-8 bg-white rounded-3xl border border-[#E8E2DC] hover:shadow-xl transition-all duration-500 hover:-translate-y-1 text-center">
                <div className="text-5xl mb-4">👨‍👩‍👧‍👦</div>
                <div className="text-5xl font-black text-emerald-700 mb-3">780</div>
                <p className="text-foreground/70 font-semibold text-sm leading-relaxed">Families Served Every Month</p>
                <div className="mt-4 h-0.5 w-12 rounded-full bg-emerald-400 mx-auto" />
              </div>

              {/* Stat 4 */}
              <div className="p-8 bg-white rounded-3xl border border-[#E8E2DC] hover:shadow-xl transition-all duration-500 hover:-translate-y-1 text-center">
                <div className="text-5xl mb-4">🎓</div>
                <div className="text-4xl font-black text-emerald-700 mb-3">Thousands</div>
                <p className="text-foreground/70 font-semibold text-sm leading-relaxed">of Students Reached Through STEM &amp; Health Education</p>
                <div className="mt-4 h-0.5 w-12 rounded-full bg-emerald-400 mx-auto" />
              </div>
            </div>
          </div>
        </section>

        {/* ── FIVE-YEAR IMPACT VISION ── */}
        <section className="py-28 px-6 bg-emerald-900 relative overflow-hidden">
          <div className="relative z-10 max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-micro text-emerald-300 tracking-widest uppercase mb-4">Five-Year Impact Vision</p>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                The Big Picture:<br />
                <span className="italic font-light text-emerald-300">What 62 School Health Hubs Could Grow</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {fiveYearStats.map((stat, i) =>
              <AnimatedStat key={i} value={stat.value} suffix={stat.suffix} label={stat.label} />
              )}
            </div>

            {/* Additional vision stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
              <div className="p-8 bg-emerald-800 border border-emerald-700 rounded-3xl text-center">
                <div className="text-5xl font-black text-white mb-3">Thousands</div>
                <p className="text-white/65 font-light text-sm leading-relaxed">Of students trained in STEM, nutrition, health literacy, and innovation</p>
              </div>
              <div className="p-8 bg-emerald-800 border border-emerald-700 rounded-3xl text-center">
                <div className="text-5xl font-black text-white mb-3">One</div>
                <p className="text-white/65 font-light text-sm leading-relaxed">Statewide network advancing preventive health education across New York</p>
              </div>
            </div>

            <div className="max-w-3xl mx-auto text-center p-8 bg-emerald-800/60 border border-emerald-700 rounded-3xl">
              <p className="text-white/80 font-light leading-relaxed text-lg italic">
                These numbers represent more than food grown or lessons taught. They represent children learning where their food comes from. Families gaining access to healthier options. Students discovering careers in healthcare, biotechnology, engineering, and innovation. Communities becoming healthier before health challenges become health crises.
              </p>
            </div>
          </div>
        </section>

        {/* ── 28-WEEK TIMELINE ── */}
        <section className="py-28 px-6 bg-[#F5F0EB]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-micro text-[#C9A96E] tracking-widest uppercase mb-4">Program Journey</p>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
                From Classroom to<br />
                <span className="italic font-light">Community Impact</span>
              </h2>
              <p className="text-lg text-foreground/55 font-light">28-Week Learning Experience</p>
            </div>

            <div className="space-y-6">
              {timelinePhases.map((phase, i) =>
              <div key={i} className={`grid grid-cols-1 lg:grid-cols-5 gap-0 rounded-3xl overflow-hidden border ${phase.borderColor} hover:shadow-xl transition-all duration-500`}>
                  {/* Phase number + image */}
                  <div className="lg:col-span-2 relative min-h-[220px]">
                    <img src={phase.img} alt={phase.imgAlt} className="w-full h-full object-cover absolute inset-0" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
                    <div className="relative z-10 p-8 h-full flex flex-col justify-end">
                      <div className={`inline-flex items-center gap-2 ${phase.color} text-white rounded-full px-4 py-1.5 text-xs font-bold tracking-wide mb-3 w-fit`}>
                        Phase {i + 1}
                      </div>
                      <p className="text-white/70 text-sm font-light">{phase.weeks}</p>
                      <h3 className="text-xl font-bold text-white leading-tight">{phase.title}</h3>
                    </div>
                  </div>
                  {/* Content */}
                  <div className={`lg:col-span-3 p-8 ${phase.lightColor}`}>
                    <p className="text-foreground/70 font-light leading-relaxed mb-6">{phase.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {phase.topics.map((topic, j) =>
                    <span key={j} className={`px-3 py-1.5 bg-white border ${phase.borderColor} ${phase.textColor} rounded-full text-xs font-semibold`}>
                          {topic}
                        </span>
                    )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── WHAT WE GROW ── */}
        <section className="py-28 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-micro text-[#C9A96E] tracking-widest uppercase mb-5">What We Grow</p>
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                  Fresh Food.<br />
                  <span className="italic font-light">Real Learning.</span><br />
                  Community Impact.
                </h2>
                <p className="text-lg text-foreground/65 font-light leading-relaxed mb-8">
                  Students cultivate a diverse range of nutritious produce. Every harvest becomes an opportunity to teach nutrition, sustainability, food access, and community care while supporting local families.
                </p>
                <div className="flex flex-wrap gap-3">
                  {produce.map((item, i) =>
                  <div key={i} className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-full hover:bg-emerald-100 transition-colors duration-200">
                      <span className="text-sm">{item.emoji}</span>
                      <span className="text-emerald-800 font-semibold text-sm">{item.name}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-[3/4] rounded-3xl overflow-hidden col-span-2">
                  <img
                    src="/assets/images/Screenshot_2026-03-25_at_10.02.03_PM-1774490528099.png"
                    alt="Beautiful array of fresh vegetables and herbs grown in school hydroponic system"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />

                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── PHOTO GALLERY 4: Partners in Impact ── */}
        <section className="py-20 px-6 bg-[#F5F0EB]">
          <div className="max-w-6xl mx-auto">
            <div className="mb-10">
              <h3 className="text-3xl font-bold text-foreground">Partners in Impact</h3>
              <p className="text-foreground/55 font-light mt-1">Schools, educators, sponsors, university partners, and community organizations.</p>
            </div>
            {/* Partner logos photo */}
            <div className="overflow-hidden rounded-3xl mb-10">
              <img
                src="/assets/images/Screenshot_2026-06-07_at_2.33.38_PM-1780857225459.png"
                alt="Partner logos including Taco Bell Foundation, DC Space Grant Consortium, Walking Softer, Camelback Ventures, and NSF Innovation Corps"
                className="w-full object-contain" />

            </div>
            {/* Programming range callout */}
            <div className="bg-white rounded-3xl border border-[#E8E2DC] p-8 md:p-10">
              <div className="flex items-start gap-4">
                <div className="text-4xl flex-shrink-0">🎓</div>
                <div>
                  <h4 className="text-xl font-bold text-foreground mb-3">From Pre-K All the Way to University</h4>
                  <p className="text-foreground/70 font-light leading-relaxed text-base">
                    The Food for Thought Foundation offers programming spanning <strong className="text-foreground/90">pre-K through K-12 and into higher education</strong>, meeting students at every stage of their journey. Our most recent university collaboration was with <strong className="text-emerald-700">USC Engineering</strong>, bringing STEM innovation and hydroponic science to the collegiate level.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── LOOKING FORWARD ── */}
        <section className="py-28 px-6 bg-[#F5F0EB]">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="/assets/images/Screenshot_2026-06-07_at_2.18.51_PM-1780856416833.png"
                    alt="Mary Bellakbira at the Food for Thought Foundation tabling event"
                    className="w-full h-full object-cover" />

                </div>
                <div className="absolute -bottom-6 -right-6 bg-[#C9A96E] text-white p-6 rounded-2xl shadow-xl max-w-[220px]">
                  <p className="font-bold text-sm">Mary Bellakbira</p>
                  <p className="text-white/80 text-xs font-light">Miss Heart of New York 2026</p>
                  <p className="text-white/70 text-xs font-light mt-1">Founder, Food for Thought Foundation</p>
                </div>
              </div>
              <div>
                <p className="text-micro text-[#C9A96E] tracking-widest uppercase mb-5">Looking Forward</p>
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-8 leading-tight">
                  Growing Healthier Futures<br />
                  <span className="italic font-light">Across New York</span>
                </h2>
                <p className="text-lg text-foreground/70 font-light leading-relaxed mb-6">
                  As Miss Heart of New York, I believe healthcare begins in our homes, schools, and communities. My vision is to expand School Health Hubs throughout New York State, creating a network of schools that serve as centers for preventive health education, food access, STEM learning, and community engagement.
                </p>
                <p className="text-base text-foreground/60 font-light leading-relaxed mb-8">
                  By bringing together educators, healthcare organizations, community partners, families, and local leaders, we can address health disparities before they become health crises. Every student deserves the opportunity to learn, lead, and live a healthy life, regardless of their ZIP code.
                </p>

                {/* Closing quote */}
                <div className="p-8 bg-[#F5F0EB] border-l-4 border-[#C9A96E] rounded-r-3xl mb-10">
                  <p className="text-xl text-foreground/80 font-light italic leading-relaxed mb-4">
                    "Through Lead with H.E.A.R.T., we are growing more than food. We are growing healthier futures."
                  </p>
                  <div>
                    <p className="font-bold text-foreground text-sm">— Mary Bellakbira</p>
                    <p className="text-foreground/50 text-xs font-light">Miss Heart of New York 2026 · Co-Founder, Food for Thought Foundation</p>
                  </div>
                </div>

                {/* Final CTAs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Link href="/contact" className="px-6 py-4 bg-foreground text-white rounded-full font-semibold text-sm tracking-wide hover:bg-foreground/85 transition-all duration-300 text-center">
                    Bring a Hub to Your Community
                  </Link>
                  <Link href="/partnerships-impact" className="px-6 py-4 bg-[#C9A96E] text-white rounded-full font-semibold text-sm tracking-wide hover:bg-[#b8935a] transition-all duration-300 text-center">
                    Become a Partner
                  </Link>
                  <Link href="/contact" className="px-6 py-4 border border-foreground/25 text-foreground rounded-full font-semibold text-sm tracking-wide hover:border-foreground/50 hover:bg-foreground/5 transition-all duration-300 text-center">
                    Donate to Support a Hub
                  </Link>
                  <Link href="/contact" className="px-6 py-4 border border-[#C9A96E]/40 text-[#C9A96E] rounded-full font-semibold text-sm tracking-wide hover:border-[#C9A96E] hover:bg-[#C9A96E]/5 transition-all duration-300 text-center">
                    Volunteer With Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>);

}